import React, { createContext, useContext, useState, useEffect } from 'react';
import { auth, db } from '../firebase/firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { loginUser, logoutUser } from '../firebase/auth';
import { doc, getDoc, setDoc, updateDoc } from 'firebase/firestore';

// Create the authentication context
const AuthContext = createContext();

// Custom hook to use the auth context
export const useAuth = () => {
  return useContext(AuthContext);
};

// Authentication provider component
export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Login function
  const login = async (email, password) => {
    const { user, error } = await loginUser(email, password);
    if (error) throw new Error(error);
    return user;
  };

  // Logout function
  const logout = async () => {
    const { error } = await logoutUser();
    if (error) throw new Error(error);
  };

  useEffect(() => {
    // Check localStorage first for cached user data
    const cachedUserData = localStorage.getItem('userData');
    if (cachedUserData) {
      try {
        const parsedUserData = JSON.parse(cachedUserData);
        setUserData(parsedUserData);
        // Don't set loading to false yet, still need to verify with Firebase
      } catch (error) {
        console.error('Error parsing cached user data:', error);
        localStorage.removeItem('userData');
      }
    }

    // Subscribe to auth state changes
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      
      if (user) {
        try {
          // Check if user document exists in Firestore
          const userRef = doc(db, 'users', user.uid);
          const userDoc = await getDoc(userRef);
          
          let userData;
          
          if (!userDoc.exists()) {
            // Create user document if it doesn't exist
            console.log('Creating new user document for:', user.uid);
            userData = {
              name: user.displayName || '',
              email: user.email || '',
              isAdmin: false,
              createdAt: new Date(),
              weddings: []
            };
            await setDoc(userRef, userData);
            userData.uid = user.uid; // Add uid to the userData object
          } else {
            // Get existing user data
            userData = { ...userDoc.data(), uid: user.uid };
            
            // Initialize weddings array if it doesn't exist
            if (!userData.weddings) {
              console.log('Adding weddings array to existing user:', user.uid);
              await updateDoc(userRef, { weddings: [] });
              userData.weddings = [];
            }
          }
          
          // Store user data in localStorage for persistence
          localStorage.setItem('userData', JSON.stringify(userData));
          setUserData(userData);
        } catch (error) {
          console.error('Error setting up user document:', error);
        }
      } else {
        // Clear localStorage when user logs out
        localStorage.removeItem('userData');
        setUserData(null);
      }
      
      setLoading(false);
    });

    // Cleanup subscription on unmount
    return unsubscribe;
  }, []);

  // Value to be provided to consuming components
  const value = {
    currentUser,
    userData,
    isAdmin: userData?.isAdmin || false,
    loading,
    login,
    logout
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
